import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';

export default function ProvidersPage() {
  const { auth } = useAuth();

  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadProviders = useCallback(async () => {
    setFetchError('');
    try {
      const res = await apiFetch('/api/admin/providers', auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProviders(Array.isArray(data) ? data : []);
    } catch {
      setFetchError("Ko'rsatuvchilarni yuklashda xatolik yuz berdi. Sahifani yangilang.");
    } finally {
      setPageLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const loadCategories = useCallback(async () => {
    setCatLoading(true);
    try {
      const res = await apiFetch('/api/admin/service-categories', auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    } finally {
      setCatLoading(false);
    }
  }, [auth.token]);

  const loadRegions = useCallback(async () => {
    setRegionsLoading(true);
    try {
      const res = await apiFetch('/regions', auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRegions(Array.isArray(data) ? data : []);
    } catch {
      setRegions([]);
    } finally {
      setRegionsLoading(false);
    }
  }, [auth.token]);

  const openModal = () => {
    setForm(EMPTY_FORM);
    setSelectedCategoryIds([]);
    setSelectedRegionId('');
    setFormError('');
    setShowModal(true);
    if (categories.length === 0) loadCategories();
    if (regions.length === 0) loadRegions();
  };

  const closeModal = () => setShowModal(false);

  const toggleCategory = (id) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!form.firstName.trim()) { setFormError("Ism maydoni majburiy"); return; }
    if (!form.lastName.trim()) { setFormError("Familiya maydoni majburiy"); return; }
    if (!form.email.trim()) { setFormError("Email maydoni majburiy"); return; }
    if (!form.phone.trim()) { setFormError("Telefon maydoni majburiy"); return; }
    if (!form.password) { setFormError("Parol maydoni majburiy"); return; }
    if (selectedCategoryIds.length === 0) {
      setFormError("Kamida bitta xizmat kategoriyasini tanlang");
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/providers', auth.token, {
        method: 'POST',
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          password: form.password,
          categoryIds: selectedCategoryIds,
          ...(selectedRegionId ? { regionId: Number(selectedRegionId) } : {}),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setFormError(d.message || "Saqlashda xatolik yuz berdi");
        return;
      }
      closeModal();
      await loadProviders();
    } catch {
      setFormError("Tarmoq xatosi. Iltimos, qayta urinib ko'ring.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>Xizmat ko'rsatuvchilar</h1>
          <p style={s.subtitle}>Ro'yxatdan o'tgan xizmat ko'rsatuvchilar</p>
        </div>
        <button style={s.addBtn} onClick={openModal}>+ Qo'shish</button>
      </div>

      {fetchError && <div style={s.errorBanner}>{fetchError}</div>}

      {pageLoading ? (
        <div style={s.centerMsg}>Yuklanmoqda...</div>
      ) : providers.length === 0 && !fetchError ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>👤</div>
          <p style={s.emptyText}>Hali xizmat ko'rsatuvchi yo'q</p>
          <p style={s.emptyHint}>Birinchi ko'rsatuvchini qo'shish uchun "+ Qo'shish" tugmasini bosing.</p>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '40px' }}>#</th>
                <th style={s.th}>F.I.O</th>
                <th style={s.th}>Email</th>
                <th style={s.th}>Telefon</th>
                <th style={s.th}>Xizmat kategoriyalari</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p, i) => (
                <tr key={p.id} style={s.tr}>
                  <td style={{ ...s.td, color: 'var(--text-faint)' }}>{i + 1}</td>
                  <td style={{ ...s.td, fontWeight: '600', color: 'var(--text)' }}>
                    {[p.firstName, p.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td style={{ ...s.td, color: 'var(--text-secondary)' }}>{p.email || '—'}</td>
                  <td style={{ ...s.td, color: 'var(--text-secondary)' }}>{p.phone || '—'}</td>
                  <td style={s.td}>
                    <CategoryBadges categories={p.categories} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.modalTitle}>Yangi xizmat ko'rsatuvchi</h2>

            <div style={s.row2}>
              <Field label="Ism" required>
                <input
                  style={s.input}
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  placeholder="Ism"
                  disabled={saving}
                />
              </Field>
              <Field label="Familiya" required>
                <input
                  style={s.input}
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  placeholder="Familiya"
                  disabled={saving}
                />
              </Field>
            </div>

            <Field label="Email" required>
              <input
                type="email"
                style={s.input}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="example@email.com"
                disabled={saving}
              />
            </Field>

            <Field label="Telefon" required>
              <input
                style={s.input}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+998901234567"
                disabled={saving}
              />
            </Field>

            <Field label="Parol" required>
              <input
                type="password"
                style={s.input}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Kamida 6 ta belgi"
                disabled={saving}
              />
            </Field>

            <Field label="Xizmat kategoriyalari" required>
              {catLoading ? (
                <div style={s.catLoading}>Kategoriyalar yuklanmoqda...</div>
              ) : categories.length === 0 ? (
                <div style={s.catEmpty}>Kategoriya yo'q — avval xizmat kategoriyalarini qo'shing</div>
              ) : (
                <div style={s.checkList}>
                  {categories.map((cat) => {
                    const checked = selectedCategoryIds.includes(cat.id);
                    return (
                      <label
                        key={cat.id}
                        style={{ ...s.checkItem, ...(checked ? s.checkItemActive : {}) }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCategory(cat.id)}
                          disabled={saving}
                          style={s.checkbox}
                        />
                        <span style={s.checkLabel}>{cat.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </Field>

            <Field label="Hudud">
              {regionsLoading ? (
                <div style={s.catLoading}>Yuklanmoqda...</div>
              ) : (
                <select
                  style={s.select}
                  value={selectedRegionId}
                  onChange={(e) => setSelectedRegionId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Hududni tanlang</option>
                  {regions.length === 0 ? (
                    <option value="" disabled>Hududlar topilmadi</option>
                  ) : (
                    regions.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))
                  )}
                </select>
              )}
            </Field>

            {formError && <div style={s.formError}>{formError}</div>}

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={closeModal} disabled={saving}>
                Bekor qilish
              </button>
              <button
                style={{ ...s.saveBtn, ...(saving ? s.disabledBtn : {}) }}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? "Saqlanmoqda..." : "Saqlash"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={s.label}>
        {label}{required && <span style={s.req}> *</span>}
      </label>
      {children}
    </div>
  );
}

function CategoryBadges({ categories }) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return <span style={{ fontStyle: 'italic', color: 'var(--border-strong)' }}>—</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {categories.map((cat) => (
        <span key={cat.id} style={s.badge}>{cat.name}</span>
      ))}
    </div>
  );
}

const EMPTY_FORM = { firstName: '', lastName: '', email: '', phone: '', password: '' };

const s = {
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  title: { margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: '14px', color: 'var(--text-muted)' },
  addBtn: {
    padding: '10px 20px',
    background: 'var(--brand)',
    color: 'var(--on-accent)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  errorBanner: {
    background: 'var(--danger-tint)',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '12px 16px',
    color: 'var(--danger)',
    fontSize: '14px',
    marginBottom: '16px',
  },
  centerMsg: { textAlign: 'center', padding: '60px', color: 'var(--text-faint)', fontSize: '15px' },
  empty: {
    textAlign: 'center',
    padding: '64px 24px',
    background: 'var(--surface)',
    borderRadius: '12px',
    border: '1px dashed #d1d5db',
  },
  emptyIcon: { fontSize: '40px', marginBottom: '12px' },
  emptyText: { margin: '0 0 6px', fontSize: '16px', fontWeight: '600', color: 'var(--text-secondary)' },
  emptyHint: { margin: 0, fontSize: '14px', color: 'var(--text-faint)' },
  tableWrap: {
    background: 'var(--surface)',
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: 'var(--text-muted)',
    background: 'var(--bg)',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '14px 16px', verticalAlign: 'middle', fontSize: '14px' },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    background: 'var(--info-tint)',
    color: 'var(--info)',
    border: '1px solid #bfdbfe',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    padding: '16px',
  },
  modalCard: {
    background: 'var(--surface)',
    borderRadius: '14px',
    padding: '28px 28px 24px',
    width: '100%',
    maxWidth: '540px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalTitle: { margin: '0 0 20px', fontSize: '18px', fontWeight: '700', color: 'var(--text)' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' },
  req: { color: 'var(--brand)' },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'var(--surface)',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'var(--surface)',
    cursor: 'pointer',
    appearance: 'none',
  },
  catLoading: { fontSize: '13px', color: 'var(--text-faint)', padding: '10px 0' },
  catEmpty: {
    fontSize: '13px',
    color: 'var(--brand)',
    background: 'var(--danger-tint)',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '10px 12px',
  },
  checkList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1.5px solid #d1d5db',
    borderRadius: '8px',
    padding: '8px',
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
    background: 'var(--bg)',
    userSelect: 'none',
  },
  checkItemActive: {
    background: 'var(--info-tint)',
    outline: '1.5px solid #bfdbfe',
  },
  checkbox: { width: '15px', height: '15px', cursor: 'pointer', accentColor: 'var(--info)' },
  checkLabel: { fontSize: '14px', color: 'var(--text)' },
  formError: {
    background: 'var(--danger-tint)',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '10px 12px',
    color: 'var(--danger)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '8px',
  },
  cancelBtn: {
    padding: '9px 18px',
    background: 'var(--surface-alt)',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  saveBtn: {
    padding: '9px 22px',
    background: 'var(--brand)',
    color: 'var(--on-accent)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  disabledBtn: { opacity: 0.65, cursor: 'not-allowed' },
};
